import _ from 'lodash';
import { Component, Inject, Vue, Watch } from 'vue-property-decorator';
import {
  LOCAL_COUNTRY,
  VERSION,
  AGENCE_TYPE,
  COMPANY_TYPE,
  ALL_COUNTRY_CODE,
  DONE_STATUS,
  STAGE_SUBMISSION,
  HMPO_UK,
  MINIO_BUCKET_ADDRESS
} from '@/constants';
import LoginService from '@/account/login.service';
import AccountService from '@/account/account.service';
import DirectusService from '@/core/directus/directus.service';
import CountryCMSModel from '@/visa-application/models/CountryCMSModel';
import LanguageCMSModel from '@/visa-application/models/LanguageCMSModel';
import NavbarCMSModel from '@/visa-application/models/NavbarCMSModel';
import LanguageCode from '@/locale/language-code';
import LoginCMSModel from '@/visa-application/models/LoginCMSModel';
import { IFormGroup } from '@/shared/model/form-group.model';
import SchengenService from '@/visa-application/services/schengen/schengen.service';
import { getLocalStorage, getQueryVariable, setLocalStorage, checkCountryCodeAndIssuerIsAvailable } from '@/shared/utils/utils';
import ApplicationCenterCMSModel from '@/visa-application/models/ApplicationCenterCMSModel';

@Component
export default class JhiNavbar extends Vue {
  public version = VERSION ? 'v' + VERSION : '';
  @Inject('loginService') private loginService: () => LoginService;
  @Inject('accountService') private accountService: () => AccountService;
  @Inject('directusService') private directusService: () => DirectusService;
  @Inject('schengenService') private schengenService: () => SchengenService;

  public client = LOCAL_COUNTRY;
  public residenceCountryCode = ALL_COUNTRY_CODE;
  public issuer = '';
  public logoStyle = MINIO_BUCKET_ADDRESS + '/logo_' + this.client + '.svg';
  public applicationCenterCmsData: ApplicationCenterCMSModel = new ApplicationCenterCMSModel();
  private currentLanguage: LanguageCode = this.$store.getters.currentLanguage;
  private languages: LanguageCMSModel[] = [];
  private defaultLanguage: LanguageCMSModel = null;
  private formGroupId = getLocalStorage('formGroupId');
  private dropdownStates = {
    visaInformation: false,
    language: false
  };
  private hamburgerOpen = false;
  private imgFallback = {
    loading: MINIO_BUCKET_ADDRESS + '/logo_' + this.client + '.svg',
    error: MINIO_BUCKET_ADDRESS + '/logo.svg'
  };

  public navbarContent?: NavbarCMSModel = null;
  public selectedLanguage: LanguageCMSModel = null;
  public loginContent?: LoginCMSModel = null;
  public formGroups: IFormGroup[] = [];

  public hideNavbarRouter = ['ApplierResidenceCountry', 'ApplierApplicationCenter'];

  @Watch('$store.getters.currentLanguage', { immediate: true, deep: true })
  public onCurrentLanguageChange(language: LanguageCode) {
    this.currentLanguage = language;
    this.setI18nLanaguage(language);

    if (this.languages && this.languages.length > 0) {
      // in case languages have not been set
      this.selectedLanguage = _.find(this.languages, { code: language });
      if (!this.selectedLanguage) {
        // wanted language is not available for this center
        // set selected language
        this.selectedLanguage = this.defaultLanguage;
        // and commit to store
        this.$store.commit('currentLanguage', this.selectedLanguage.code);
        this.setI18nLanaguage(language);
      }
    }

    this.getLoginContentFromCMS();
    this.getContentFromCMS();
  }

  @Watch('$route', { deep: true })
  public onRouteChange(route: any) {
    this.hamburgerOpen = false;
    if (route.params.issuer) {
      if (!checkCountryCodeAndIssuerIsAvailable(route.params.issuer, 'issuer')) {
        this.$router.push({ name: 'NotFound' });
      }
      this.issuer = route.params.issuer;
      this.getContentFromCMS();
      this.getLoginContentFromCMS();
      this.imgLogoNavbar();
    } else {
      this.issuer = getLocalStorage('issuer') ? getLocalStorage('issuer') : undefined;
    }

    if (route.params.residenceCountryCode) {
      if (!checkCountryCodeAndIssuerIsAvailable(route.params.residenceCountryCode)) {
        this.$router.push({ name: 'NotFound' });
      }
      this.residenceCountryCode = route.params.residenceCountryCode;
    } else {
      this.residenceCountryCode = getLocalStorage('residenceCountryCode') ? getLocalStorage('residenceCountryCode') : ALL_COUNTRY_CODE;
    }

    if (route.params.formGroupId) {
      this.formGroupId = route.params.formGroupId;
    }

    this.getLanguagesForResidenceCountry();
  }

  public imgLogoNavbar(): void {
    const filters = {
      code: {
        eq: this.issuer
      }
    };
    this.directusService()
      .getCollection<ApplicationCenterCMSModel>(ApplicationCenterCMSModel, 'application_centers', ['*.*'], filters, this.currentLanguage)
      .then((result: ApplicationCenterCMSModel[]) => {
        let logoNavbar = '';
        result.forEach(function(item) {
          if (item.website_header_logo === null) {
            logoNavbar = MINIO_BUCKET_ADDRESS + '/logo_' + this.client + '.svg';
          } else {
            logoNavbar = item.website_header_logo.data.url;
          }
        });
        this.logoStyle = logoNavbar;
      })
      .catch(error => console.error(error));
  }

  public getContentFromCMS(): void {
    const filter = {
      'application_center.code': {
        eq: this.issuer
      }
    };

    // Page with hidden navigation bar
    if (this.hideNavbarRouter.indexOf(this.$route.name) === -1) {
      this.directusService()
        .getCollection<NavbarCMSModel>(NavbarCMSModel, 'navbar', ['*.*.*'], filter, this.currentLanguage)
        .then((response: NavbarCMSModel[]) => {
          this.navbarContent = _.head(response);
        })
        .catch(error => console.error(error));
    }
  }

  public getLoginContentFromCMS(): void {
    this.directusService()
      .getCollection<LoginCMSModel>(LoginCMSModel, 'login', ['*.*'], undefined, this.currentLanguage)
      .then((response: LoginCMSModel[]) => {
        this.loginContent = _.head(response);
      })
      .catch(error => console.error(error));
  }

  public get authenticated(): boolean {
    return this.$store.getters.authenticated;
  }

  public get swaggerEnabled(): boolean {
    return this.$store.getters.activeProfiles.indexOf('swagger') > -1;
  }

  public get inProduction(): boolean {
    return this.$store.getters.activeProfiles.indexOf('prod') > -1;
  }

  public getLanguagesForResidenceCountry(): void {
    const filter = {
      code: {
        eq: this.residenceCountryCode
      }
    };

    this.directusService()
      .getCollection<ApplicationCenterCMSModel>(
        ApplicationCenterCMSModel,
        'application_centers',
        ['*.*'],
        { code: { eq: this.issuer } },
        this.currentLanguage
      )
      .then((result: ApplicationCenterCMSModel[]) => {
        this.applicationCenterCmsData = result[0];
      })
      .catch(error => console.error(error));

    this.directusService()
      .getCollection<CountryCMSModel>(CountryCMSModel, 'country', ['*.*.*.*'], filter)
      .then((result: CountryCMSModel[]) => {
        this.defaultLanguage = result[0].defaultLanguage;
        this.languages = _.orderBy(result[0].availableLanguages, ['name'], ['asc']);

        const parameter_language_exists = _.filter(this.languages, ['code', getQueryVariable('lang')]);
        if (getQueryVariable('lang').length > 0 && parameter_language_exists.length === 1) {
          this.$store.commit('currentLanguage', parameter_language_exists[0].code);
          this.onCurrentLanguageChange(parameter_language_exists[0].code);

          // Modify the url address bar
          const url = window.location.href;
          const valiable = url.split('?')[0];
          window.history.pushState({}, '0', valiable);
        } else {
          let currLang;
          if (this.$store.getters.languageFrom === 'code') {
            currLang = result[0].defaultLanguage.code;
          } else {
            currLang = this.$store.getters.currentLanguage;
          }
          // const currLang1 = this.$store.getters.currentLanguage ? this.$store.getters.currentLanguage : result[0].defaultLanguage.code;
          this.$store.commit('currentLanguage', currLang);
          this.onCurrentLanguageChange(currLang);
        }
      })
      .catch(error => console.error(error));
  }

  public subIsActive(input) {
    const paths = Array.isArray(input) ? input : [input];
    return paths.some(path => {
      return this.$route.path.indexOf(path) === 0; // current path starts with this path string
    });
  }

  public toggleDropdown(dropdownName) {
    const state = this.dropdownStates[dropdownName];
    this.closeDropdowns();
    Vue.set(this.dropdownStates, dropdownName, !state);
  }

  public closeDropdowns() {
    Object.keys(this.dropdownStates).forEach(key => (this.dropdownStates[key] = false));
  }

  public changeLanguage(newLanguage: string): void {
    this.$store.commit('currentLanguage', newLanguage);
    this.setI18nLanaguage(newLanguage);
    this.closeDropdowns();
  }

  public setI18nLanaguage(language: string) {
    this.$i18n.locale = language;
    setLocalStorage('lang', language);
  }

  public isActiveLanguage(key: string): boolean {
    return key === this.$store.getters.currentLanguage;
  }

  public logout(): void {
    this.loginService()
      .logout()
      .then(response => {
        this.$store.commit('logout');
        const data = response.data;
        let logoutUrl = data.logoutUrl;
        // if Keycloak, uri has protocol/openid-connect/token
        if (logoutUrl.indexOf('/protocol') > -1) {
          logoutUrl =
            logoutUrl + '?redirect_uri=' + window.location.origin + '/visa/' + this.residenceCountryCode + '/' + this.issuer + '/home';
        } else {
          // Okta
          logoutUrl =
            logoutUrl +
            '?id_token_hint=' +
            data.idToken +
            '&post_logout_redirect_uri=' +
            window.location.origin +
            '/visa/' +
            this.residenceCountryCode +
            '/' +
            this.issuer +
            '/home';
        }
        window.location.href = logoutUrl;
      });
  }

  public openLogin(): void {
    this.loginService().goToLogin(this.residenceCountryCode, this.issuer);
  }

  public openRegister(): void {
    this.loginService().goToRegister(this.residenceCountryCode, this.issuer);
  }

  public openMyApplication(): void {
    if (this.$store.getters.userType) {
      this.goToFormGroupPage();
    } else {
      this.schengenService()
        .getUserInfo(LOCAL_COUNTRY, this.issuer)
        .then(res => {
          this.$store.commit('userType', res.ug_type);
          this.goToFormGroupPage();
        })
        .catch(() => {
          this.$router.push({
            name: 'Welcome',
            params: {
              residenceCountryCode: this.residenceCountryCode,
              issuer: this.issuer
            }
          });
        });
    }
  }

  public goToFormGroupPage(): void {
    this.$router.push({
      name: 'FormGroup',
      params: {
        residenceCountryCode: this.residenceCountryCode,
        issuer: this.issuer
      }
    });
  }

  public goToLink(link: string) {
    if (link.startsWith('http')) {
      window.open(link);
    } else {
      window.location.href = link;
    }
  }

  public hasAnyAuthority(authorities: any): boolean {
    return this.accountService().hasAnyAuthority(authorities);
  }

  public hasIssuer(): boolean {
    const pathname = window.location.pathname;
    return pathname.search(this.$store.getters.issuer) > 0;
  }

  public onMainLogoClick(): void {
    if (this.issuer && checkCountryCodeAndIssuerIsAvailable(this.issuer, 'issuer')) {
      this.$router.push({
        name: 'ApplicationCenterHome',
        params: {
          residenceCountryCode: this.residenceCountryCode,
          issuer: this.issuer
        }
      });
    } else {
      this.$router.push({
        name: 'ApplierResidenceCountry'
      });
    }
  }
}
